import * as yup from 'yup'
import QuickForm from "../../Forms/QuickForm/QuickForm"
import { FieldGeneratorFieldType } from "../../FormFields/FieldGenerator/FieldGenerator"
import { PAYMENT_SOURCE_OPTIONS } from "../../Bookings/ExtendStayModal/ExtendStayModal"
import { BookingLinkedServiceCategory, parseLinkedServicePayload } from "../../Forms/BookingForm/BookingLinkedServiceHelpers"
import timeService from '../../../services/time/timeService'

type BookingLinkedServiceFormProps = {
    initialData: any,
    onSubmit: (linkedServiceData: any) => void
    onCancel?: () => void
}

const halfWidth = { xs: 12, md: 6 }

const numberField = (fieldName: string, fieldLabel: string): FieldGeneratorFieldType => ({
    fieldType: 'number',
    fieldName,
    fieldLabel,
    layout: halfWidth,
})

export const EditBookingRemoteLockLinkedServiceForm = (props: BookingLinkedServiceFormProps) => {
    const { initialData, onSubmit, onCancel } = props

    return (
        <QuickForm
            initialData={{
                userId: '',
                pin: '',
                ...parseLinkedServicePayload(initialData),
            }}
            onSubmit={onSubmit}
            onCancel={onCancel}
            validationSchema={yup.object().shape({
                userId: yup.string().required('User ID is required'),
                pin: yup.string().required('PIN is required'),
            })}
            fields={[
                { fieldType: 'text', fieldName: 'userId', fieldLabel: 'Remote Lock User ID' },
                { fieldType: 'text', fieldName: 'pin', fieldLabel: 'PIN' },
            ]}
        />
    )
}

export const EditBookingExtendStayLinkedServiceForm = (props: BookingLinkedServiceFormProps) => {
    const { initialData, onSubmit, onCancel } = props
    const parsed = parseLinkedServicePayload(initialData)

    return (
        <QuickForm
            initialData={{
                ...parsed,
                newCheckOut: parsed.newCheckOut ? new Date(parsed.newCheckOut) : null,
                originalCheckOut: parsed.originalCheckOut ? new Date(parsed.originalCheckOut) : null,
                paymentSource: parsed.paymentSource ?? '',
                createdBy: parsed.createdBy ?? '',
            }}
            onSubmit={(values: any) => {
                const payload: any = {
                    ...values,
                    newCheckOut: timeService.dateToMillis(values.newCheckOut),
                    originalCheckOut: timeService.dateToMillis(values.originalCheckOut),
                    newPricePerNight: +values.newPricePerNight,
                    originalPricePerNight: +values.originalPricePerNight,
                    newEarnings: +values.newEarnings,
                    originalEarnings: +values.originalEarnings,
                    extensionEarnings: +values.extensionEarnings,
                }
                if (!payload.paymentSource) delete payload.paymentSource
                if (!payload.createdBy) delete payload.createdBy
                onSubmit(payload)
            }}
            onCancel={onCancel}
            validationSchema={yup.object().shape({
                newCheckOut: yup.date().nullable().required('New check-out is required'),
                originalCheckOut: yup.date().nullable().required('Original check-out is required'),
                newPricePerNight: yup.number().required('Required').min(0),
                originalPricePerNight: yup.number().required('Required').min(0),
                newEarnings: yup.number().required('Required').min(0),
                originalEarnings: yup.number().required('Required').min(0),
                extensionEarnings: yup.number().required('Required').min(0),
                paymentSource: yup.string().nullable(),
                createdBy: yup.string().nullable(),
            })}
            fields={[
                { fieldType: 'date', fieldName: 'originalCheckOut', fieldLabel: 'Original Check-Out', layout: halfWidth },
                { fieldType: 'date', fieldName: 'newCheckOut', fieldLabel: 'New Check-Out', layout: halfWidth },
                numberField('originalPricePerNight', 'Original Price Per Night'),
                numberField('newPricePerNight', 'New Price Per Night'),
                numberField('originalEarnings', 'Original Earnings'),
                numberField('newEarnings', 'New Earnings'),
                numberField('extensionEarnings', 'Extension Earnings'),
                {
                    fieldType: 'select',
                    fieldName: 'paymentSource',
                    fieldLabel: 'Payment Source',
                    placeholder: '- None -',
                    options: PAYMENT_SOURCE_OPTIONS,
                    layout: halfWidth,
                },
                { fieldType: 'text', fieldName: 'createdBy', fieldLabel: 'Created By' },
            ]}
        />
    )
}

export const EditBookingEarlyLateLinkedServiceForm = (props: BookingLinkedServiceFormProps) => {
    const { initialData, onSubmit, onCancel } = props
    const productId = String(initialData?.linkedService).startsWith('earlyCheckIn') ? 'earlyCheckIn' : 'lateCheckOut'
    const label = productId === 'earlyCheckIn' ? 'Early Check-In' : 'Late Check-Out'

    return (
        <QuickForm
            initialData={{
                productId,
                timeDifference: '',
                ...parseLinkedServicePayload(initialData),
            }}
            onSubmit={(values: any) => onSubmit({ ...values, timeDifference: String(values.timeDifference) })}
            onCancel={onCancel}
            validationSchema={yup.object().shape({
                timeDifference: yup.number().required('Hours are required').moreThan(0, 'Hours must be greater than 0'),
            })}
            fields={[
                {
                    fieldType: 'number',
                    fieldName: 'timeDifference',
                    fieldLabel: `${label} Hours`,
                    subText: 'Number of hours the time is extended. This is also part of the entry name.',
                },
            ]}
        />
    )
}

export const EditBookingParkingLinkedServiceForm = (props: BookingLinkedServiceFormProps) => {
    const { initialData, onSubmit, onCancel } = props
    const parsed = parseLinkedServicePayload(initialData)

    return (
        <QuickForm
            initialData={{
                productId: 'parking',
                bukibleId: '',
                bukinId: '',
                parkingImage: '',
                ...parsed,
                parkingStart: new Date(parsed.parkingStart),
                parkingEnd:  new Date(parsed.parkingEnd),
            }}
            onSubmit={(values: any) => onSubmit({
                ...values,
                parkingStart: timeService.dateToIsoString(values.parkingStart),
                parkingEnd: timeService.dateToIsoString(values.parkingEnd),
            })}
            onCancel={onCancel}
            validationSchema={yup.object().shape({
                bukibleId: yup.string().required('Bukible ID is required'),
                bukinId: yup.string().required('Bukin ID is required'),
                parkingImage: yup.string().nullable(),
                parkingStart: yup.date().nullable().required('Parking start is required'),
                parkingEnd: yup.date().nullable().required('Parking end is required'),
            })}
            fields={[
                { fieldType: 'text', fieldName: 'bukibleId', fieldLabel: 'Bukible ID', layout: halfWidth, subText: 'Bukible and Bukin IDs are part of the entry name.' },
                { fieldType: 'text', fieldName: 'bukinId', fieldLabel: 'Bukin ID', layout: halfWidth },
                { fieldType: 'date', fieldName: 'parkingStart', fieldLabel: 'Parking Start', layout: halfWidth },
                { fieldType: 'date', fieldName: 'parkingEnd', fieldLabel: 'Parking End', layout: halfWidth },
                { fieldType: 'text', fieldName: 'parkingImage', fieldLabel: 'Parking Image URL', extra: { imagePreview: true } },
            ]}
        />
    )
}

export const bookingLinkedServiceForms: Record<BookingLinkedServiceCategory, (props: BookingLinkedServiceFormProps) => JSX.Element> = {
    remoteLock: EditBookingRemoteLockLinkedServiceForm,
    extendStay: EditBookingExtendStayLinkedServiceForm,
    lateCheckOut: EditBookingEarlyLateLinkedServiceForm,
    earlyCheckIn: EditBookingEarlyLateLinkedServiceForm,
    parking: EditBookingParkingLinkedServiceForm,
}
